import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Reusable CRUD dialog.
 *
 * fields: [{ name, label, type: "text"|"number"|"textarea"|"email"|"date"|"select"|"switch", options?, required?, help? }]
 */
export default function CrudDialog({
  open, onOpenChange, title, fields, initial, endpoint, method = "POST", onSaved, testIdPrefix = "crud",
}) {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(initial || {});
  }, [open, initial]);

  const set = (name, val) => setForm((f) => ({ ...f, [name]: val }));

  // Fields can hide themselves based on other values, and their `options`
  // can be a function that depends on the current form state — used for
  // cascading selects (e.g. Subsidiary filtered by Master).
  const resolvedFields = fields
    .map((f) => ({
      ...f,
      options: typeof f.options === "function" ? f.options(form) : f.options,
    }))
    .filter((f) => (typeof f.hidden === "function" ? !f.hidden(form) : !f.hidden));

  const invalid = resolvedFields.some((f) => f.required && !form[f.name]);

  const submit = async () => {
    setSubmitting(true);
    try {
      // Filter out empty strings — treat as null
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v === undefined) continue;
        payload[k] = v;
      }
      const res = method === "PATCH"
        ? await api.patch(endpoint, payload)
        : await api.post(endpoint, payload);
      toast.success(method === "PATCH" ? "Updated" : "Created");
      // Give every field a chance to run a post-submit hook (e.g. assign an
      // employee) with access to the created record.
      for (const f of resolvedFields) {
        if (typeof f.afterSubmit === "function") {
          try { await f.afterSubmit(res.data, form); }
          catch (_) { /* non-fatal */ }
        }
      }
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {resolvedFields.map((f) => (
            <div key={f.name}>
              <Label className="text-xs">
                {f.label}
                {f.required && <span className="text-rose-500 ml-1">*</span>}
              </Label>
              {f.type === "custom" ? (
                <div className="mt-1">
                  {f.render?.({ form, set, testId: `${testIdPrefix}-${f.name}` })}
                </div>
              ) : f.type === "textarea" ? (
                <Textarea
                  className="mt-1"
                  value={form[f.name] || ""}
                  onChange={(e) => set(f.name, e.target.value)}
                  data-testid={`${testIdPrefix}-${f.name}`}
                />
              ) : f.type === "select" ? (() => {
                // Radix <SelectItem/> forbids empty-string values. Callers sometimes
                // pass `{ value: "", label: "— (None)" }` for optional selects; we
                // translate empties to a sentinel here and back to "" on change so
                // every page keeps working without edits.
                const NULL_SENTINEL = "__none__";
                const opts = (f.options || []).map((o) => {
                  const raw = typeof o === "object" ? o : { value: o, label: o };
                  const isEmpty = raw.value === "" || raw.value === null || raw.value === undefined;
                  return { value: isEmpty ? NULL_SENTINEL : String(raw.value), label: raw.label ?? String(raw.value) };
                });
                const cur = form[f.name];
                const isCurEmpty = cur === "" || cur === null || cur === undefined;
                const hasEmptyOpt = opts.some((o) => o.value === NULL_SENTINEL);
                const selected = isCurEmpty ? (hasEmptyOpt ? NULL_SENTINEL : undefined) : String(cur);
                return (
                  <Select value={selected} onValueChange={(v) => {
                    const nextVal = v === NULL_SENTINEL ? "" : v;
                    set(f.name, nextVal);
                    if (typeof f.onSelect === "function") {
                      f.onSelect(nextVal, form, set);
                    }
                  }}>
                    <SelectTrigger className="h-10 mt-1" data-testid={`${testIdPrefix}-${f.name}`}>
                      <SelectValue placeholder={f.placeholder || "Select…"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {opts.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              })() : f.type === "switch" ? (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!form[f.name]}
                    onChange={(e) => set(f.name, e.target.checked)}
                    data-testid={`${testIdPrefix}-${f.name}`}
                  />
                  <span className="text-sm text-slate-700">{form[f.name] ? "Yes" : "No"}</span>
                </div>
              ) : (
                <Input
                  type={f.type || "text"}
                  className="mt-1 h-10"
                  value={form[f.name] ?? ""}
                  onChange={(e) => set(f.name, f.type === "number" ? Number(e.target.value) : e.target.value)}
                  placeholder={f.placeholder}
                  data-testid={`${testIdPrefix}-${f.name}`}
                />
              )}
              {f.help && <div className="text-[11px] text-slate-500 mt-1">{f.help}</div>}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-slate-900" disabled={invalid || submitting} onClick={submit} data-testid={`${testIdPrefix}-save-btn`}>
            {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            {method === "PATCH" ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Confirm delete dialog. */
export function ConfirmDeleteDialog({ open, onOpenChange, title, description, onConfirm, testId, confirmLabel }) {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testId}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={confirm} disabled={busy}>
            {busy ? "Working…" : (confirmLabel || "Delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
